import { connect } from "react-redux";
import React, { useEffect, memo, useRef } from "react";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import get from "lodash/get";
import find from "lodash/find";
import reduce from "lodash/reduce";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import history from "utils/history";
import { Box } from "ui-components";
import Spinner, { styles } from "components/Spinner";
import usePrefetchComponents from "hooks/usePrefetchComponents";

import {
  renderPageBanner,
  renderPageComponents,
  renderPageStepper,
  renderPageFaqs
} from "smart-components/RenderPageComponent";
import Session from "session";
import {
  STANDALONE_WORKFLOW_PATHS,
  paths,
  PARTNERS_CHANNEL,
  STANDALONE_WORKFLOW_ROUTES,
  IS_PATHS_SECURED,
  ERROR_CODES,
  CONSENT_TYPES,
  PAYMENT_PARTNERS,
  PAYMENT_CARD_TYPES
} from "utils/constants";
import {
  gotoUrl,
  isMobile,
  getChannelFromUrl,
  isWorkflowChannel,
  getOnSubmitActionHandler,
  handleOnLoadAction,
  getOTTHeader,
  createConsentPayload
} from "utils/helpers";
import {
  makeSelectWorkflowWithProps,
  makeSelectAppSettings,
  makeSelectCurrencyFormat,
  makeSelectPlanName,
  makeSelectPlanTextName,
  makeSelectPayTodayItems,
  makeSelectPayMonthlyItems,
  makeSelectPhoneVariant,
  makeSelectPhonePaymentOption,
  makeSelectUserDetails,
  makeSelectIsFetchingAnyData,
  makeSelectThemeColors,
  makeSelectIsModalView,
  makeSelectModalType,
  makeSelectOttExpiresIn,
  makeSelectOttResendAllowed,
  makeSelectOttResendAllowedAfter,
  makeSelectProfileValidations,
  makeSelectIsUserLoggedIn,
  makeSelectPlanType,
  makeSelectHaveModalError,
  makeSelectEnabledLocale,
  makeSelectAuthToken,
  makeSelectisguestSignInUser,
  makeSelectPaymentOptions
} from "ducks/selectors";
import {
  isWebview,
  isNative,
  setLandingPage,
  resetLandingPage,
  getLandingPage,
  setLandingPageVisited,
  getLandingPageVisited,
  removeItem,
  getRedirectURL,
  getDeviceId,
  getExternalId,
  getItem
} from "utils/localStorage";
import {
  requestOtt,
  requestOttResend,
  setData,
  clearData,
  disableResendActivation,
  fetchLocale,
  setConsentRecord,
  requestGuestOtt,
  userLogout,
  clearStore
} from "ducks/actions";
import { makeSelectOttRequestId } from "ducks/selectors/forms/userLoginOTPForm";
import Alert from "react-s-alert";

const SmartRender = pageProps => {
  const { workflow, appSettings, messageData, match, themeColors } = pageProps;
  useEffect(() => {
    const channel = getChannelFromUrl();
    const channelWorkflow = find(
      workflow,
      ({ path }) => path && path.match(`/${channel}`)
    );
    const landingPage = get(channelWorkflow, "path");
    isWorkflowChannel() ? setLandingPage(landingPage) : resetLandingPage();
    if (window.location.pathname === landingPage) {
      setLandingPageVisited(true);
    }
    if (window.location.pathname === paths.LANDING_PAGE) {
      const landingPagePath = get(workflow, "0.path", "");
      if (landingPagePath && window.location.pathname !== landingPagePath) {
        Session.getDeviceId();
        gotoUrl(landingPagePath, true, { reset: undefined });
      }
    } else {
      if (
        isWorkflowChannel() &&
        !getLandingPageVisited() &&
        !includes(STANDALONE_WORKFLOW_PATHS, window.location.pathname) &&
        !includes(STANDALONE_WORKFLOW_ROUTES, match?.path) &&
        !IS_PATHS_SECURED // disable landing page visited as login will manage workflow
      ) {
        gotoUrl(getLandingPage(), true, { reset: undefined });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow]);

  const scrolledRef = useRef();

  useEffect(() => {
    // handleOnLoadAction run if page has onload prop from CCM
    handleOnLoadAction(pageProps);
    if (!isEmpty(window.location.hash)) {
      const id = window.location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        scrolledRef.current = true;
      }
    }
    // resetLocale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const channel = getChannelFromUrl();
  const urlParts = get(window, "location.pathname", "").split("/");
  const firstUrlPart = urlParts.length > 1 ? urlParts[2] : "web";
  let currentWorkflowPage;
  if (channel === PARTNERS_CHANNEL && firstUrlPart !== "success") {
    currentWorkflowPage = find(
      workflow,
      page => page.path === "/partners/:partnerID"
    );
  } else {
    currentWorkflowPage = find(
      workflow,
      page => page.path === window.location.pathname
    );
  }
  const maintenanceRedirectPage = get(appSettings, [
    "maintenanceRedirectPages",
    window.location.pathname
  ]);

  if (appSettings?.maintenanceMode) {
    gotoUrl(
      maintenanceRedirectPage ||
        appSettings?.maintenancePage ||
        paths.MAINTENANCE
    );
  }

  const preloadPathList = get(currentWorkflowPage, "preloadPath", []);
  const nextPageComponents = reduce(
    preloadPathList,
    (accumulator, preloadPath) => {
      const nextWorkflowPage = find(
        workflow,
        page => page.path === preloadPath
      );
      const components = get(nextWorkflowPage, "components", []);
      return [...accumulator, ...components];
    },
    []
  );
  usePrefetchComponents(nextPageComponents);

  const redirectPage =
    get(currentWorkflowPage, "redirect") || maintenanceRedirectPage;
  if (redirectPage) {
    gotoUrl(redirectPage);
  }

  const pageComponents = get(currentWorkflowPage, "components");
  const pageBanner = get(currentWorkflowPage, "banner");
  const pageStepper = get(currentWorkflowPage, "stepper");
  const pageFaqs = get(currentWorkflowPage, "faqs");
  const themeVariant = get(currentWorkflowPage, "themeVariant", "main");
  const padding = get(currentWorkflowPage, "padding", [6, 8]);
  const pageStyles = get(currentWorkflowPage, "styles", {});
  const screenshotEnabled = get(currentWorkflowPage, "screenshot", false);
  const flexMode = get(currentWorkflowPage, "flex", true);
  // Putting a validation saying the path will be available if the previous path is from one of the mentioned in allowedPrevPaths
  const allowedPrevPaths = get(currentWorkflowPage, "allowedPrevPaths");
  if (allowedPrevPaths) {
    const prevPath = window.location?.pathTracking?.previous;
    const currentPath = window.location?.pathTracking?.current;

    // if previous path is not in allowed paths take the user back to previous path
    // prevPath !== currentPath to allow reloads from same page
    if (prevPath !== currentPath && allowedPrevPaths.indexOf(prevPath) === -1) {
      return history.push(prevPath);
    }
  }

  const containerProps = {
    variant: themeVariant,
    padding: padding
  };

  const flexProps = !flexMode
    ? {}
    : {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        overflow: "hidden"
      };
  return (
    <div data-testid="smart-render">
      {renderPageBanner({ pageBanner })}
      {renderPageStepper({ pageStepper, padding })}
      <Box id="smart-render-box" {...containerProps} {...flexProps} sx={{ ...pageStyles }}>
        {isEmpty(pageComponents) ? (
          <Spinner size={200} sx={styles.fullPage} />
        ) : (
          renderPageComponents({
            pageComponents,
            pageProps,
            appSettings,
            screenshotEnabled
          })
        )}
      </Box>
      {renderPageFaqs({ pageFaqs, messageData, themeColors })}
    </div>
  );
};

const mapStateToProps = (_, ownProps) =>
  createStructuredSelector({
    workflow: makeSelectWorkflowWithProps(ownProps),
    appSettings: makeSelectAppSettings(),
    currencyFormat: makeSelectCurrencyFormat(),
    webview: isWebview,
    isWebview: isWebview,
    isNative: isNative,
    isMobile: isMobile,
    planName: makeSelectPlanName(),
    planTextName: makeSelectPlanTextName(),
    payTodayItems: makeSelectPayTodayItems(),
    payMonthlyItems: makeSelectPayMonthlyItems(),
    phoneVariant: makeSelectPhoneVariant(),
    phonePaymentOption: makeSelectPhonePaymentOption(),
    userDetails: makeSelectUserDetails(),
    isFetchingAnyData: makeSelectIsFetchingAnyData(),
    themeColors: makeSelectThemeColors(),
    modalType: makeSelectModalType(),
    isModalView: makeSelectIsModalView(),
    requestId: makeSelectOttRequestId(),
    expiresIn: makeSelectOttExpiresIn(),
    ottResendAllowed: makeSelectOttResendAllowed(),
    ottResendAllowedAfter: makeSelectOttResendAllowedAfter(),
    profileValidations: makeSelectProfileValidations(),
    redirectUrl: () => getRedirectURL(),
    isLoggedIn: makeSelectIsUserLoggedIn(),
    planType: makeSelectPlanType(),
    haveModalError: makeSelectHaveModalError(),
    defaultLangauga: makeSelectEnabledLocale(),
    authToken: makeSelectAuthToken(),
    isGuestSignUp: makeSelectisguestSignInUser(),
    paymentOptions: makeSelectPaymentOptions()
  });

export const mapDispatchToProps = dispatch => {
  return {
    requestOtp: (navigate, payload, data, stateProps) => {
      const selectedPaymentInfo =
        JSON.parse(getItem("selectedPaymentOption")) || "";
      if (
        get(selectedPaymentInfo, "payment_partner") ===
          PAYMENT_PARTNERS.EASYPAISA &&
        get(selectedPaymentInfo, "payment_variant.card_type") ===
          PAYMENT_CARD_TYPES.EASYPAISA_LINK
      ) {
        setTimeout(() => {
          // navigation delayed 500ms for updating redux state correctly
          isFunction(navigate) && navigate();
        }, 500);
        return;
      }
      const ottType = get(data, "ottType");
      const ottTypeFailed = get(data, "ottTypeFailed");
      const params = { ottType };
      const securityHeaderRequired = get(data, "securityHeaderRequired", false); // include additional headers to requestOtp API call
      const headers = securityHeaderRequired && getOTTHeader(ottType, payload);
      if (ottType === "SERVICE_TERMINATE_OTT") {
        let concentPayload = createConsentPayload(
          "SERVICE_TERMINATE",
          "TERMS_AND_CONDITIONS",
          null,
          null,
          CONSENT_TYPES.IMPLICIT
        );
        dispatch(setConsentRecord(concentPayload, stateProps.authToken));
      }
      dispatch(
        requestOtt(params, headers, (res, error) => {
          const success = isEmpty(error);
          if (success && get(res, "requestId")) {
            removeItem(`${data.timerKey}.timer.startrecord`);
            dispatch(
              setData({
                key: "isModalView",
                data: { isModalView: true, type: ottType }
              })
            );
            dispatch(
              setData({
                key: "haveModalError",
                data: { haveModalError: false, description: "" }
              })
            );
            dispatch(disableResendActivation());
          } else {
            if (ottTypeFailed) {
              dispatch(
                setData({
                  key: "isModalView",
                  data: { isModalView: true, type: ottTypeFailed }
                })
              );
            } else {
              error?.errorId === ERROR_CODES.maximumRetryOtt &&
                setTimeout(() => {
                  if (isWebview()) {
                    gotoUrl(paths.CLOSEWEBVIEW, false, {}, true);
                  } else {
                    gotoUrl(
                      get(data, "ottBlockedRedirection", paths.MANAGE_ORDER)
                    );
                  }
                }, 0);
              if (error?.errorId === ERROR_CODES.emailAlreadyTaken) {
                dispatch(
                  setData({
                    key: "isModalView",
                    data: { isModalView: true, type: "EMAIL_ALREADY_TAKEN" }
                  })
                );
              }
            }
          }
        })
      );
    },
    fetchLogoutUser: (stateProps, callback) => {
      const headers = {};
      const params = {
        device_id: getDeviceId(),
        user_id: stateProps.external_id
      };
      dispatch(userLogout(headers, params, callback));
    },
    requestGuestOtp: (navigate, payload, data, stateProps) => {
      const ottType = get(data, "ottType");
      const params = {
        ottType,
        email: payload?.email,
        channel: getChannelFromUrl(),
        "external-auth-id": getExternalId(),
        contactNumber: payload?.contact_number
      };
      const securityHeaderRequired = get(data, "securityHeaderRequired", false); // include additional headers to requestOtp API call
      const headers = securityHeaderRequired && getOTTHeader(ottType, payload);
      dispatch(
        requestGuestOtt(params, headers, (res, error) => {
          const success = isEmpty(error);
          if (success && get(res, "requestId")) {
            removeItem(`${data.timerKey}.timer.startrecord`);
            dispatch(disableResendActivation());
            setTimeout(() => {
              let element = document.querySelectorAll(
                'input[data-testid="number-box"]'
              );
              if (element.length > 0) {
                element[0].scrollIntoView(true);
                let scrolledY = window.scrollY;
                if (scrolledY) {
                  window.scroll({
                    top: scrolledY - 150,
                    left: 0,
                    behavior: "smooth"
                  });
                }
              }
            }, 1000);
          } else {
            if (error?.errorId === ERROR_CODES.guestUserRealEmailAlreadyTaken) {
              dispatch(
                setData({
                  key: "isModalView",
                  data: { isModalView: true, type: "EMAIL_ALREADY_TAKEN" }
                })
              );
            }
            if (error?.errorId === ERROR_CODES.maximumRetryOtt) {
              dispatch(
                clearData([
                  "userAuthenticate",
                  "requestGuestOtt",
                  "validateGuestOtt",
                  "lockedNumber",
                  "filteredNumbers",
                  "selectedNumberCategory"
                ])
              );
              setTimeout(() => {
                if (isWebview()) {
                  gotoUrl(paths.CLOSEWEBVIEW, false, {}, true);
                } else {
                  gotoUrl(
                    get(data, "ottBlockedRedirection", paths.MANAGE_ORDER)
                  );
                }
              }, 0);
            }
          }
        })
      );
    },
    navigateToManageOrder: () => {
      gotoUrl(paths.MANAGE_ORDER, false);
    },
    goToLogin: (navigate, payload, data, stateProps) => {
      dispatch(clearStore());
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      setTimeout(() => {
        dispatch(clearStore());
        gotoUrl(`${paths.LOG_IN.replace(":channel", "web")}?reset=true`);
        // }));
      }, 1500);
    },
    hideModal: () => {
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: false, type: "" }
        })
      );
      dispatch(
        setData({
          key: "haveModalError",
          data: { haveModalError: false, description: "" }
        })
      );
    },
    showSmartModal: (navigate, payload, data, stateProps) => {
      const modalType = data?.modalType;
      modalType &&
        dispatch(
          setData({
            key: "isModalView",
            data: { isModalView: true, type: modalType }
          })
        );
    },
    setModalError: desc => {
      dispatch(
        setData({
          key: "haveModalError",
          data: { haveModalError: true, description: desc }
        })
      );
    },
    clearModalError: () => {
      dispatch(
        setData({
          key: "haveModalError",
          data: { haveModalError: false, description: "" }
        })
      );
    },
    requestResendOtp: (params, key, successMessage) => {
      dispatch(
        requestOttResend(params, (response, errors) => {
          const success = !errors;
          if (success) {
            dispatch(disableResendActivation());
            removeItem(`${key}.timer.startrecord`);
            Alert.success(
              `<h1 style="padding-right: 20px">${successMessage}</h1>`
            );
          } else if (
            errors?.errorId === ERROR_CODES.ottExpired ||
            errors?.errorId === ERROR_CODES.ottVerificationBlocked ||
            errors?.errorId === ERROR_CODES.resendOttBlockled
          ) {
            dispatch(
              setData({
                key: "isModalView",
                data: { isModalView: false, type: "" }
              })
            );
          } else if (errors?.errorId === ERROR_CODES.maximumRetryOtt) {
            setTimeout(() => {
              if (isWebview()) {
                gotoUrl(paths.CLOSEWEBVIEW, false, {}, true);
              } else {
                gotoUrl(paths.MANAGE_ORDER);
              }
            }, 0);
          } else {
            // props.setErrorMessage(response.message);
            console.error("Request OTP failed. Cause: ", errors);
          }
        })
      );
    },
    clearSelectedAddons: () => {
      dispatch(clearData(["selectedAddons"]));
    },
    resetLocale: () => {
      dispatch(fetchLocale());
    }
  };
};

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  ottExpiresIn: ownProps.ottExpiresIn
    ? ownProps.ottExpiresIn
    : stateProps.expiresIn, // override smartRender expiresIn if container already has it's own ottExpiresIn
  ottResendAllowed: ownProps.ottResendAllowed
    ? ownProps.ottResendAllowed
    : stateProps.ottResendAllowed,
  onSubmit: (navigate, payload, onSubmitOverride, data) => {
    const combinedProps = {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      onSubmitOverride
    };
    const onSubmitActionHandler = getOnSubmitActionHandler(combinedProps);
    isFunction(onSubmitActionHandler)
      ? onSubmitActionHandler(navigate, payload, data, stateProps)
      : isFunction(combinedProps.onSubmit)
      ? combinedProps.onSubmit(navigate, payload)
      : navigate();
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(withConnect, memo)(SmartRender);
